import { Formik, Form, FieldArray } from 'formik';
import * as Yup from 'yup';
import FieldWrapper from '../../../components/formInputs/FieldWrapper';
import SectionHeader from '../../../components/SectionHeader';
import Button from '../../../components/buttons/Button';
import { useRef } from 'react';
import { getMedia, scrollToError } from '../../../utils/HelperFunctions';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ICityForm } from '../types/interfaces';
import useFormsIntegrationHelpers from '../../../hooks/useFormsIntegrationHelpers';
import FormDynamicSkeleton from '../../../components/loaders/FormDynamicSkeleton';
import useModelSelectOptions from '../../../hooks/useModelSelectOptions';
import { customStyles } from '../../../utils/SelectStyles';
import { deleteIcon } from '../../../config/variables';

const CreateCityForm = () => {
  const { id } = useParams();
  const formRef = useRef(null);
  const { t, i18n } = useTranslation();

  const { apiData, getDataLoading, handleSubmit, submitLoading } = useFormsIntegrationHelpers({
    queryKey: ['cities', id],
    invalidateQueryKey: ['cities'],
    id,
    singleGetApi: `/admin/cities/${id}`,
    addApi: '/admin/cities',
    editApi: `/admin/cities/${id}`,
    itemName: t('city'),
    listRoute: '/cities'
  });

  const { modelOptions: deliveryOptionsOptions } = useModelSelectOptions({
    labelKey: `name.${i18n.language}`,
    valueKey: 'id',
    modelRoute: 'admin/delivery-options',
    queryKey: ['delivery-options']
  });

  const validationSchema = Yup.object({
    name: Yup.object().shape({
      ar: Yup.string().required(t('required')).min(3, t('name_short')),
      en: Yup.string().required(t('required')).min(3, t('name_short'))
    }),
    delivery_options: Yup.array().of(
      Yup.object().shape({
        id: Yup.string().required(t('required')),
        price: Yup.string().required(t('required'))
      })
    )
  });

  const initialValues: ICityForm = {
    ...apiData,

    name: {
      en: apiData?.name?.en || '',
      ar: apiData?.name?.ar || ''
    },
    delivery_options: apiData?.delivery_options || [],

    code: apiData?.code || '',
    icon: getMedia(apiData?.media, 'icon')?.url || ''
  };

  if (getDataLoading) return <FormDynamicSkeleton sections={[[{ columns: 2, items: 2 }], [{ columns: 2, items: 2 }]]} buttons={1} />;

  return (
    <div className={'form_section'} ref={formRef}>
      <Formik
        validateOnMount
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setErrors }) => {
          let value = { ...values, _method: 'POST' };
          if (id) {
            value = { ...value, _method: 'PUT' };
          }
          handleSubmit({ ...value }, setErrors);
        }}
      >
        {(formik) => (
          <>
            <Form>
              <SectionHeader title={t('city_details')}>
                <div className="inputs_group">
                  <FieldWrapper title={t('name_en')} inputName={`name.en`} inputPlaceholder={t('name_en')} input customPadding />
                  <FieldWrapper title={t('name_ar')} inputName={`name.ar`} inputPlaceholder={t('name_ar')} input customPadding />
                </div>
              </SectionHeader>
              <SectionHeader title={t('delivery_options')} customStyle="last">
                <FieldWrapper>
                  <FieldArray
                    name="delivery_options"
                    render={(arrayHelpers) => (
                      <div>
                        {formik?.values?.delivery_options?.map((_, index: number) => (
                          <div key={index} className="inputs_group">
                            <FieldWrapper
                              title={t('delivery_option')}
                              inputName={`delivery_options.${index}.id`}
                              inputPlaceholder={t('delivery_option')}
                              customPadding
                              options={deliveryOptionsOptions?.filter(
                                (option: any) =>
                                  !formik?.values?.delivery_options?.some(
                                    (deliveryOption: any, deliveryOptionIndex: number) => deliveryOption.id === option.value && deliveryOptionIndex !== index
                                  )
                              )}
                              value={deliveryOptionsOptions?.find((option: any) => option.value === formik?.values?.delivery_options?.[index]?.id)}
                              selectStyle={customStyles}
                              select
                            />
                            <FieldWrapper title={t('price')} inputName={`delivery_options.${index}.price`} inputPlaceholder={t('price')} input customPadding />
                            <div className="actions">
                              <div className="action_btn pointer delete" onClick={() => arrayHelpers.remove(index)}>
                                {deleteIcon}
                              </div>
                            </div>
                          </div>
                        ))}

                        {formik?.values?.delivery_options?.length < deliveryOptionsOptions?.length && (
                          <div
                            className="add-time-button"
                            onClick={() => {
                              arrayHelpers.push({
                                id: '',
                                price: ''
                              });
                            }}
                          >
                            <svg width="16" height="16" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="M8.9375 12.625V9.8125H6.125C5.80859 9.8125 5.5625 9.56641 5.5625 9.25C5.5625 8.96875 5.80859 8.6875 6.125 8.6875H8.9375V5.875C8.9375 5.59375 9.18359 5.3125 9.5 5.3125C9.78125 5.3125 10.0625 5.59375 10.0625 5.875V8.6875H12.875C13.1562 8.6875 13.4375 8.96875 13.4375 9.25C13.4375 9.56641 13.1562 9.8125 12.875 9.8125H10.0625V12.625C10.0625 12.9414 9.78125 13.1875 9.5 13.1875C9.18359 13.1875 8.9375 12.9414 8.9375 12.625ZM18.5 9.25C18.5 14.2422 14.457 18.25 9.5 18.25C4.50781 18.25 0.5 14.2422 0.5 9.25C0.5 4.29297 4.50781 0.25 9.5 0.25C14.457 0.25 18.5 4.29297 18.5 9.25ZM9.5 1.375C5.14062 1.375 1.625 4.92578 1.625 9.25C1.625 13.6094 5.14062 17.125 9.5 17.125C13.8242 17.125 17.375 13.6094 17.375 9.25C17.375 4.92578 13.8242 1.375 9.5 1.375Z"
                                fill="#211D33"
                              />
                            </svg>
                            {t('add_delivery_option')}
                          </div>
                        )}
                      </div>
                    )}
                  />
                </FieldWrapper>
              </SectionHeader>
              <div className="form_button reverse">
                <Button
                  loading={submitLoading}
                  onClick={() => {
                    scrollToError(!formik.isValid, formRef);
                  }}
                >
                  <span className="bold">{t('save')}</span>
                </Button>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default CreateCityForm;
